import { useMemo } from 'react'

import { Loader2 } from 'lucide-react'

import { ReportEmpty } from '@/components/reports/body'
import { formatters } from '@Components/hierarchical-report/Layout'
import HierarchicalTable from '@Components/hierarchical-report/Table'

import { buildMetricColumn } from '../utils/metricColumns'
import { useCustomReportStore } from '../store'

function requestErrorMessage(error) {
  const data = error?.response?.data
  const message = typeof data === 'string' ? data : data?.message || data?.error

  return message || error?.message || 'Could not run the report.'
}

function normalizeTableData(data) {
  if (!data) return { children: [] }
  if (Array.isArray(data)) return { children: data }
  if (data.children) return data
  return { children: [data] }
}

function TableResult({ data, metrics = [], groupby = [], collection }) {
  const tableData = useMemo(() => normalizeTableData(data), [data])

  const columns = useMemo(() => {
    const metricKeys = metrics.map(metric => (typeof metric === 'string' ? metric : metric.key))

    return [
      {
        dataField: 'value',
        text: groupby?.length ? groupby.join(' / ') : 'Name',
        customSort: true,
        sortValue: row => row.name || row.keyString || row.value,
        formatter: formatters.name,
      },
      ...metricKeys.map(key => buildMetricColumn(key, collection)),
    ]
  }, [metrics, groupby, collection])

  if (!tableData.children?.length) {
    return <ReportEmpty><div className="font-semibold py-8 text-center">No results to show.</div></ReportEmpty>
  }

  return <div className="w-full max-w-full overflow-x-auto">
    <HierarchicalTable columns={columns} data={tableData} />
  </div>
}

export default function ReportTable() {
  const {
    config: { value: config },
    table: { data, status, error },
  } = useCustomReportStore()

  if (status === 'loading') {
    return (
      <div className="flex justify-center items-center h-40">
        <Loader2 size={24} className="animate-spin" />
      </div>
    )
  }

  if (status === 'error') {
    return <div className="font-semibold py-8 text-center twy-text-danger">
      {requestErrorMessage(error)}
    </div>
  }

  return (
    <TableResult
      data={data}
      metrics={config.metrics}
      groupby={config.groupby}
      collection={config.collection}
    />
  )
}
